import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { combineLatestWith, map, Observable, of, pipe, switchMap } from 'rxjs';

import { ScannedCode, ScannedCodeType } from 'app/features/scan-flow/models';
import { selectAppModeIfDefined } from 'app/features/settings/state/settings/settings.selectors';
import { defaultActivity } from 'app/shared/features/activity-selector/models';
import { selectBatchByNumber } from 'app/shared/features/batch-selector/state/batch.selectors';
import { selectActivityByNumber } from 'app/state/activities/activity.selectors';
import {
    filterByCodeType,
    getActivityCode,
    getActivityNumberFromScannedCodes,
    getBatchNumberFromScannedCodes,
} from 'src/app/features/scan-flow/utils/scanned-code-list.utils';

import { scanFlowFeatureKey, ScanFlowState } from './scan-flow.reducer';
import { getAllowedCodeTypes, getScanFlowCodeDefinitionsForActivity } from './scan-flow.utils';

export const selectScanFlowState = createFeatureSelector<ScanFlowState>(scanFlowFeatureKey);

export const selectScanFlowRegistrationData = createSelector(selectScanFlowState, (state) => state.data);

export const selectScanFlowInitializationProgress = createSelector(
    selectScanFlowState,
    (state) => state.initializationProgress,
);

export const selectScanFlowWaitingForUserInput = createSelector(
    selectScanFlowState,
    (state) => state.waitingForUserInput,
);

export const selectScanFlowCodes = createSelector(
    selectScanFlowRegistrationData,
    (data) => data.scannedCodes,
);

export const selectScanFlowAmountResult = createSelector(
    selectScanFlowRegistrationData,
    (data) => data.amountResult,
);

export const selectScanFlowProductBoxRegistrations = createSelector(
    selectScanFlowRegistrationData,
    (data) => data.productBoxRegistrations,
);
export const selectScanFlowRowSelection = createSelector(
    selectScanFlowRegistrationData,
    (data) => data.rowSelection,
);

export const selectScanFlowRowSelectionStatusByRowNumber = (rowNumber: number) =>
    createSelector(
        selectScanFlowRowSelection,
        (rowSelection) => rowSelection.find((row) => row.rowNumber === rowNumber)?.registrationStatus,
    );

// -- Scan Flow: Scanned Codes in Registration Data --
export const selectScanFlowCodesByCodeType = (codeType: ScannedCodeType) =>
    pipe(
        select(selectScanFlowCodes),
        map((scannedCodes) => filterByCodeType(scannedCodes, codeType)),
    );

const mapToScanFlowAllowedCodeTypes = (scannedCodes: ScannedCode[]) =>
    pipe(
        mapScanFlowCodesToListDefinitions(scannedCodes),
        map((scanFlowCodeListDefinitions) =>
            getAllowedCodeTypes({ scanFlowCodeListDefinitions, scannedCodes }),
        ),
    );

export const selectScanFlowAllowedCodeTypes = (store: Observable<object>) =>
    store.pipe(
        select(selectScanFlowCodes),
        switchMap((scannedCodes) => store.pipe(mapToScanFlowAllowedCodeTypes(scannedCodes))),
    );

export const selectScanFlowActivity = (store: Observable<object>) =>
    store.pipe(
        select(selectScanFlowCodes),
        switchMap((scannedCodes) => store.pipe(mapScanFlowCodesToActivity(scannedCodes))),
    );

export const selectScanFlowBatch = (store: Observable<object>) =>
    store.pipe(
        select(selectScanFlowCodes),
        switchMap((scannedCodes) => store.pipe(mapScanFlowCodesToBatch(scannedCodes))),
    );

export const mapScanFlowCodesToBatch = (scannedCodes: ScannedCode[]) => (store: Observable<object>) => {
    const batchNumber = getBatchNumberFromScannedCodes(scannedCodes);
    return batchNumber ? store.pipe(selectBatchByNumber(batchNumber)) : of(undefined);
};

export const mapScanFlowCodesToActivity = (scannedCodes: ScannedCode[]) => (store: Observable<object>) => {
    const activityNumber = getActivityNumberFromScannedCodes(scannedCodes);
    const activityCode = getActivityCode(scannedCodes);

    if (activityCode?.systemCode?.isSystemCode) {
        return of({
            ...defaultActivity(),
            name: activityCode.systemCode.name ?? '',
        });
    }
    return activityNumber ? store.pipe(selectActivityByNumber(activityNumber)) : of(undefined);
};

export const selectScanFlowCodeListDefinitions = (store: Observable<object>) =>
    store.pipe(
        select(selectScanFlowCodes),
        switchMap((scannedCodes) => store.pipe(mapScanFlowCodesToListDefinitions(scannedCodes))),
    );

export const mapScanFlowCodesToListDefinitions =
    (scannedCodes: ScannedCode[]) => (store: Observable<object>) => {
        return mapScanFlowCodesToActivity(scannedCodes)(store).pipe(
            combineLatestWith(store.pipe(selectAppModeIfDefined)),
            map(([activity, appMode]) => ({
                activity,
                appMode,
            })),
            map(({ activity, appMode }) => getScanFlowCodeDefinitionsForActivity(activity, appMode)),
        );
    };
